<template>
  <div
    class="map-pane-content unit-details-pane"
    :class="{ anonymous: shouldHideBehindPaywall }"
    :style="unitDetailsPaneStyle">
    <!-- UNIT HEADER-->
    <div class="unit-header">
      <Screen />
      <div class="name-and-id">
        <span>
          {{ activeUnit.name }}
        </span>
        <small
          v-show="showExternalId"
          style="word-break: break-word">
          {{ activeUnit.externalID }}
        </small>
      </div>
      <div
        v-if="props.activeUnits?.length > 1"
        class="paging flex-v">
        <div class="flex-h">
          <button
            class="btn btn-xs btn-no-text btn-secondary-reverse-hollow mr-2"
            @click="previous">
            <Previous2 />
          </button>
          <button
            class="btn btn-xs btn-no-text btn-secondary-reverse-hollow"
            @click="next">
            <Next2 />
          </button>
        </div>
        <span>
          {{ unitPosition }}
        </span>
      </div>
    </div>

    <!-- UNIT CONTENT-->
    <div class="scrollable-content padded">
      <div
        v-if="shouldHideBehindPaywall"
        class="paywall">
        <p>
          {{ $t('messages.paywall') }}
        </p>
      </div>
      <!-- LOCATION IMAGE-->
      <div
        v-if="imageUri"
        class="location-image-container">
        <img
          class="street-view-image"
          :src="imageUri"
          :alt="activeUnit.name" />
        <div
          v-if="showGoogleStreetViewWarning"
          class="street-view-warning">
          <span>Google Street View imagery</span>
        </div>
      </div>

      <!-- GENERAL INFO-->
      <div class="subsection">
        <div class="subsection-header">
          {{ capitalize($t('messages.generalInformation')) }}
        </div>
        <div class="subsection-row logo">
          <label>{{ capitalize($t('names.mediaOwner', { n: 1 })) }}</label>
          <img
            v-if="activeUnitMediaOwner?.logoUrl"
            class="media-owner-logo"
            :src="activeUnitMediaOwner.logoUrl"
            :alt="activeUnitMediaOwner?.name" />
          <span v-else-if="activeUnitMediaOwner">
            {{ activeUnitMediaOwner?.name }}
          </span>
        </div>

        <div class="subsection-row">
          <label>{{ capitalize($t('names.site', { n: 1 })) }}</label>
          <span>{{ activeUnit.site?.name }}</span>
        </div>

        <div
          v-if="activeUnit?.mediaTypeID"
          class="subsection-row">
          <label>{{ capitalize($t('names.venueType', { n: 1 })) }}</label>
          <span>{{ activeUnitMediaTypeName }}</span>
        </div>

        <div
          v-if="activeUnitNetwork"
          class="subsection-row">
          <label>{{ capitalize($t('names.network', { n: 1 })) }}</label>
          <span>{{ activeUnitNetwork?.name }}</span>
        </div>

        <div
          v-if="isSupplyOrganization && activeUnit.lastHeardFromAt"
          class="subsection-row">
          <label>{{ capitalize($t('messages.lastSeen')) }}</label>
          <span>{{ activeUnit.timeEllapsedSinceLastHeardFrom }}</span>
        </div>

        <div class="subsection-row">
          <label>{{ capitalize($t('names.floorCPM', { n: 1 })) }}</label>
          <span>{{ activeUnit.floorCPMFormatted }}</span>
        </div>
      </div>

      <!-- SUPPORTED CONTENT -->
      <div class="subsection">
        <div class="subsection-header">
          {{ capitalize($t('messages.supportedContent')) }}
        </div>
        <div class="supported-content">
          <div v-if="activeUnit.allowImage">
            <Image />
            <span>{{ capitalize($t('names.image', { n: 2 })) }}</span>
          </div>
          <div v-if="activeUnit.allowVideo">
            <Video />
            <span>{{ capitalize($t('messages.video', { n: 2 })) }}</span>
          </div>
        </div>
      </div>

      <!-- DIMENSION -->
      <div class="subsection screen-dimensions-subsection">
        <div class="subsection-header">{{ capitalize($t('names.dimension', { n: 2 })) }} (px)</div>
        <div class="screen-dimensions-container">
          <div class="screen-dimensions-frame">
            <div
              class="screen-dimensions"
              :style="activeUnitScreenDimensionStyle">
              <span>{{ formatNumber(activeUnit.screenWidth, '0,0') }}</span>
              <span>x</span>
              <span>{{ formatNumber(activeUnit.screenHeight, '0,0') }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- PLAYBACK -->
      <div class="subsection">
        <div class="subsection-header">
          {{ capitalize($t('names.playback', { n: 1 })) }}
        </div>
        <div class="subsection-row">
          <label>{{ capitalize($t('names.spotLength', { n: 1 })) }}</label>
          <span>{{ activeUnit.spotLengthFormatted }}</span>
        </div>
        <div
          v-if="isSupplyOrganization"
          class="subsection-row">
          <label>{{ capitalize($t('names.loopLength', { n: 1 })) }}</label>
          <span>{{ activeUnit.loopLengthFormatted }}</span>
        </div>
        <div
          v-if="isSupplyOrganization"
          class="subsection-row">
          <label>{{ capitalize($t('names.operatingHour', { n: 2 })) }}</label>
          <span v-sanitize-html="activeUnit.operatingHoursDescription" />
        </div>
        <div class="subsection-row">
          <label>{{ capitalize($t('names.timezone', { n: 1 })) }}</label>
          <span>{{ activeUnit.timezone }}</span>
        </div>
        <div
          v-if="activeUnit.unitLanguages?.length > 0"
          class="subsection-row">
          <label>{{ capitalize($t('names.language', { n: 2 })) }}</label>
          <span>{{ activeUnit.languagesDescription }}</span>
        </div>
        <div
          v-if="activeUnit.iabBlockCategories?.length > 0"
          class="subsection-row">
          <label>{{ capitalize($t('names.iabCategory', { n: 2 })) }}</label>
          <span>{{ iabCategoriesDescription }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed, getCurrentInstance, onMounted, ref } from 'vue';

  import Image from '@/common/icons/colored/Image.vue';
  import Video from '@/common/icons/colored/Video.vue';
  import Next2 from '@/common/icons/monochrome/Next2.vue';
  import Previous2 from '@/common/icons/monochrome/Previous2.vue';
  import Screen from '@/common/icons/monochrome/Screen.vue';
  import { Network, Unit } from '@/common/models';
  import MediaOwner from '@/common/models/media-owner/MediaOwner';
  import { useMediaOwners, useMediaTypes, useNetworks } from '@/org-app/services/tanstack/queries';
  import { waitForPendingQueries } from '@/org-app/services/tanstack/queryUtils';
  import formatNumber from '@/utils/NumberFormatter';
  import { mapOrgAppState } from '@/utils/StateMapping';
  import i18n from '@/vueI18n';

  interface Props {
    activeUnits: Unit[];
    hideBehindPaywall: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    hideBehindPaywall: false,
  });
  const activeUnit = ref(props.activeUnits[0]);
  const mapContainerActualHeight = ref(100);

  const orgAppState = mapOrgAppState();

  const isSupplyOrganization = computed<boolean>(() => orgAppState?.organization?.isSupplyOrganization || false);
  const hasPlanSubscription = computed<boolean>(() => orgAppState?.organizationSettings?.enablePlansFeature);
  const isActiveUnitOwnedByUserOrg = computed<boolean>(
    () => activeUnit.value?.ownerID === orgAppState?.organization?.id,
  );
  const shouldHideBehindPaywall = computed<boolean>(
    () => !hasPlanSubscription.value && !isActiveUnitOwnedByUserOrg.value && props.hideBehindPaywall,
  );
  const networksQuery = useNetworks();
  const mediaOwnersQuery = useMediaOwners();
  const mediaTypesQuery = useMediaTypes();
  const mediaTypesFlattened = computed(() => {
    const getChildren = (mt) => [mt, ...mt.children.map((x) => getChildren(x)).flat()];
    return mediaTypesQuery.items.value.map((mt) => getChildren(mt)).flat();
  });
  const activeUnitMediaTypeName = computed<string>(
    () => mediaTypesFlattened.value.find((x) => x.id === activeUnit.value.mediaTypeID)?.name || null,
  );
  const activeUnitMediaOwner = computed<MediaOwner>(
    () => mediaOwnersQuery.items.value.find((mo) => mo.id === activeUnit.value.ownerID) || null,
  );
  const activeUnitNetwork = computed<Network>(
    () => networksQuery.items.value.find((n) => n.id === activeUnit.value.networkID) || null,
  );
  const imageUri = computed<string>(() => activeUnit.value.imageUri || activeUnit.value.streetViewImageUrl);
  const showExternalId = computed<boolean>(() => activeUnit.value.name !== activeUnit.value.externalID);
  const showGoogleStreetViewWarning = computed<boolean>(
    () => !activeUnit.value.imageUri && activeUnit.value.isStreetViewImageAvailable,
  );
  const unitPosition = computed<string>(
    () =>
      `${props.activeUnits.indexOf(activeUnit.value) + 1} ${i18n.global.t('messages.outOf')} ${props.activeUnits.length}`,
  );
  const unitDetailsPaneStyle = computed<string>(() => `max-height: ${mapContainerActualHeight.value - 15}px`);
  const activeUnitScreenDimensionStyle = computed<string>(() => {
    const maxWidth = 220;
    const maxHeight = 110;
    let useHorizontalLayout = false;
    let width = activeUnit.value?.screenWidth ?? maxWidth;
    let height = activeUnit.value?.screenHeight ?? maxHeight;
    if (activeUnit.value.screenWidth > activeUnit.value.screenHeight) {
      height *= maxWidth / activeUnit.value.screenWidth;
      width = maxWidth;

      if (width / height > 1.5) {
        useHorizontalLayout = true;
      }
    } else {
      width *= maxHeight / activeUnit.value.screenHeight;
      height = maxHeight;
    }
    const style = `width: ${width}px; height: ${height}px;`;
    return useHorizontalLayout ? `${style}flex-direction: row;` : style;
  });
  const iabCategoriesDescription = computed<string>(() =>
    activeUnit.value.iabBlockCategories.map((x) => x.description).join(', '),
  );

  function setMapContainerHeight(): void {
    const instance = getCurrentInstance();
    mapContainerActualHeight.value = instance?.parent?.refs?.mapboxMap?.$el?.offsetHeight || 100;
  }

  function previous(): void {
    activeUnit.value =
      props.activeUnits[
        (props.activeUnits.length + props.activeUnits.indexOf(activeUnit.value) - 1) % props.activeUnits.length
      ];
  }

  function next(): void {
    activeUnit.value = props.activeUnits[(props.activeUnits.indexOf(activeUnit.value) + 1) % props.activeUnits.length];
  }

  async function init() {
    setMapContainerHeight();

    await waitForPendingQueries([mediaOwnersQuery, mediaTypesQuery, networksQuery]);
  }

  onMounted(async () => {
    await init();
  });
</script>

<style lang="scss" scoped>
  @use 'sass:color';
  @use '@/styles/variables.scss' as *;

  .map-pane-content {
    font-size: 0.9em;
    display: flex;
    flex-direction: column;
    -webkit-overflow-scrolling: touch;
    transform-origin: top;
    animation-name: scaleVertical;
    animation-duration: 0.5s;
    animation-timing-function: cubic-bezier(0.2, 1, 0.2, 1);
    animation-fill-mode: backwards;

    &.unit-details-pane {
      .scrollable-content {
        background-color: var(--panesBackgroundColor);

        &.padded {
          padding: 1.6rem;
        }

        overflow-y: auto;
      }

      &.anonymous {
        .paywall {
          background: var(--primaryColor);
          padding: 1rem 2rem;

          p {
            color: var(--primaryColorReverse);
            font-weight: bold;
            margin: 0;
          }
        }

        .subsection-row {
          span {
            background: var(--textMutedColorLight);
            filter: blur(0.3rem);
            font-size: 0;
            height: 1.4rem;
          }
        }

        .supported-content {
          div {
            width: 3.2rem;
            height: 3.2rem;
            background: var(--textMutedColorLight);
            filter: blur(0.3rem);

            svg,
            span {
              display: none;
            }
          }
        }

        .screen-dimensions {
          span:nth-of-type(1),
          span:nth-of-type(3) {
            font-size: 0 !important;
            height: 2rem;
            width: 4rem;
            background: var(--textMutedColorLight);
            filter: blur(0.3rem);
          }
        }
      }
    }

    .unit-header {
      background-color: var(--secondaryColor);
      color: var(--secondaryColorReverse);
      padding: 1.6rem 2rem;
      font-weight: 700;
      font-size: 1.4rem;
      display: flex;
      flex-direction: row;
      flex: 1 0 auto;
      position: relative;
      overflow: visible;
      box-shadow: 0 1px 6px rgba(0, 0, 0, 0.25);
      z-index: 1;
      line-height: 1.8rem;

      .name-and-id {
        display: flex;
        flex-grow: 1;
        flex-direction: column;

        small {
          margin-top: 0.3rem;
          font-weight: 400;
        }
      }

      .paging {
        align-items: flex-end;
        font-size: 0.8em;

        .btn {
          margin-bottom: 0.2rem;
        }

        span {
          text-align: right;
        }
      }

      > svg {
        fill: var(--secondaryColorReverse);
        stroke: var(--secondaryColorReverse);
        min-width: 2.4rem;
        width: 2.4rem;
        height: 2.4rem;
        margin-right: 1.6rem;
      }
    }

    .location-image-container {
      position: relative;
      display: flex;
      flex-direction: column;
      flex-shrink: 0;

      .street-view-image {
        width: 100%;
      }

      .street-view-warning {
        color: rgba(0, 0, 0, 0.5);
        background-color: color.adjust(#fff, $alpha: 0.5) !important;
        position: absolute;
        bottom: 0.8rem;
        left: 50%;
        width: 18rem;
        padding: 0.3rem 0.6rem;
        border-radius: 1.6rem;
        margin: auto auto auto -9rem;
        text-align: center;

        span {
          text-shadow: 0 0 0.5rem #fff;
        }
      }
    }

    .supported-content {
      display: flex;
      justify-content: space-evenly;

      div {
        display: flex;
        flex-direction: column;
        align-items: center;

        svg {
          width: 3.2rem;
          height: 3.2rem;
        }

        span {
          font-size: 0.9em;
          font-weight: 600;
          margin-top: 0.3rem;
        }
      }
    }

    .screen-dimensions-subsection {
      background: var(--background);
      box-shadow: inset 0 0 2.4rem rgba(0, 0, 0, 0.15);
      border-color: color.adjust($borders-light, $lightness: -10%) !important;
      padding-top: 1.6rem !important;

      .screen-dimensions-container {
        display: flex;
        align-content: center;
        justify-content: center;

        .screen-dimensions-frame {
          border: solid 0.1rem rgba(0, 0, 0, 0.15);
          background: var(--secondaryColorReverseFaded50);
          padding: 0.8rem;
          box-shadow: $small-shadow;

          .screen-dimensions {
            border: solid 0.1rem var(--secondaryColorFaded50);
            background: var(--secondaryColorReverse);
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;

            span {
              color: var(--secondaryColor);
              font-size: 1.1rem;
              text-align: center;

              &:nth-of-type(2n + 1) {
                font-size: 1.6rem;
              }

              &:nth-of-type(2n) {
                margin: 0 1rem;
              }
            }
          }
        }
      }
    }
  }

  .media-owner-logo {
    max-width: 50%;
    max-height: 4.8rem;
    margin: auto 0 auto auto;
  }
</style>
